import { useState, useRef, useCallback } from 'react'

/**
 * Hook for copying text to clipboard with a temporary "copied" indicator.
 * Used across list pages (Matches List, Live Control) for copying publishing IDs.
 *
 * @param resetDelay - How long (ms) to show the "copied" state before resetting. Default: 3000
 * @returns copiedId - The ID of the most recently copied item (null when not showing)
 * @returns handleCopy - Call with (itemId, textToCopy) to copy and show indicator
 */
export function useCopyToClipboard(resetDelay = 3000) {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = useCallback(
    (itemId: string, text: string) => {
      navigator.clipboard.writeText(text)
      setCopiedId(itemId)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setCopiedId(null), resetDelay)
    },
    [resetDelay]
  )

  return { copiedId, handleCopy }
}
